import { CanActivateFn, CanActivateChildFn, CanMatchFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '../../features/auth/services/auth.service';
import { NotificationService } from '../services/notification.service';
import { inject } from '@angular/core';
import { Observable, map, catchError, of } from 'rxjs';

function checkPermissions(
  route: any,
  authService: AuthService,
  router: Router,
  notification: NotificationService
): boolean | UrlTree {
  const requiredPermissions = route.data?.['permissions'] as string[] | undefined;
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasPermission = requiredPermissions.some((p) => authService.hasPermission(p));
    if (!hasPermission) {
      notification.error('You do not have permission to access this page');
      return router.createUrlTree(['/error/access-denied']);
    }
  }
  return true;
}

/**
 * Optimized auth handler with cached expiration checks and background refresh.
 * - Uses cached token expiration to avoid repeated crypto operations
 * - Implements grace period for recently expired tokens
 * - Triggers background refresh instead of blocking navigation
 */
function handleAuth(
  route: any,
  returnUrl: string,
  authService: AuthService,
  router: Router,
  notification: NotificationService
): boolean | UrlTree | Observable<boolean | UrlTree> {
  const token = authService.getToken();
  const refreshToken = authService.getRefreshToken();

  // Use cached expiration check for performance
  const isTokenExpired = token ? authService.isTokenExpiredCached() : true;

  // If token exists and is not expired, proceed immediately
  if (token && !isTokenExpired) {
    return checkPermissions(route, authService, router, notification);
  }

  // Token expired but refresh token exists
  if (token && isTokenExpired && refreshToken) {
    // Check if within grace period (expired less than 5 minutes ago)
    const expirationInfo = authService.getTokenExpirationInfo();

    if (expirationInfo.gracePeriod) {
      // Within grace period - allow navigation, refresh in background
      authService.refreshTokenInBackground();
      return checkPermissions(route, authService, router, notification);
    }

    // Critical expiration (> 5 min) - must refresh first
    return authService.refreshToken().pipe(
      map(() => checkPermissions(route, authService, router, notification)),
      catchError(() => of(router.createUrlTree(['/login'], { queryParams: { returnUrl } })))
    );
  }

  // No token but refresh token exists - try to refresh
  if (!token && refreshToken) {
    return authService.refreshToken().pipe(
      map(() => checkPermissions(route, authService, router, notification)),
      catchError(() => of(router.createUrlTree(['/login'], { queryParams: { returnUrl } })))
    );
  }

  // No valid auth, redirect to login page
  return router.createUrlTree(['/login'], { queryParams: { returnUrl } });
}

export const authGuardFn: CanActivateFn = (route, state) => {
  return handleAuth(
    route,
    state.url,
    inject(AuthService),
    inject(Router),
    inject(NotificationService)
  );
};

export const authChildGuardFn: CanActivateChildFn = authGuardFn;

export const authMatchGuardFn: CanMatchFn = (route, segments) => {
  const url = '/' + segments.map((s) => s.path).join('/');
  return handleAuth(route, url, inject(AuthService), inject(Router), inject(NotificationService));
};
